import { useEffect, useState } from 'react';
import { apiFetch } from '../auth/apiFetch';
import { getToken } from '../auth/PasswordGate';
import { useActualsVersion } from './actualsBus';
import type { TransactionsScope } from './useTransactions';

export type BudgetActualsState =
  | { status: 'loading'; totals: Record<string, number>; counts: Record<string, number> }
  | { status: 'ready'; totals: Record<string, number>; counts: Record<string, number> }
  | {
      status: 'error';
      totals: Record<string, number>;
      counts: Record<string, number>;
      error: string;
    };

// 'YYYY-MM' identifying which month to fetch actuals for. Omit to fetch the
// current calendar month (the API's default behavior).
export type BudgetActualsMonth = string | undefined;

// Compute the inclusive `to` date for a given 'YYYY-MM'. Postgres DATE
// comparison stays correct because we always use the literal end-of-month
// day (handles 28/29/30/31 across all months and Feb leap years).
function endOfMonth(monthKey: string): string {
  const [yStr, mStr] = monthKey.split('-');
  const y = Number(yStr);
  const m = Number(mStr);
  // Day 0 of the next month = last day of this month.
  const last = new Date(Date.UTC(y, m, 0));
  const lastDay = String(last.getUTCDate()).padStart(2, '0');
  return `${monthKey}-${lastDay}`;
}

// Fetches per-budget-category actual spending for a given month (defaults
// to the current calendar month). The hook degrades silently — on error
// or when the user has no Plaid items the returned `totals` is just an
// empty object, so callers can render the planned-only view unchanged.
//
// `scope` picks which household member's attribution to compute (omit to
// default to the caller). Scoping to another household member returns
// empty totals — their personal accounts are privacy-hidden.
export function useBudgetActuals(
  scope?: TransactionsScope,
  month?: BudgetActualsMonth,
): BudgetActualsState {
  const [state, setState] = useState<BudgetActualsState>({
    status: 'loading',
    totals: {},
    counts: {},
  });

  const scopeKey = scope ? `user:${scope.viewAsUserId}` : 'self';
  const monthKey = month ?? '';
  const version = useActualsVersion();

  useEffect(() => {
    let cancelled = false;
    const token = getToken();
    if (!token) {
      setState({ status: 'ready', totals: {}, counts: {} });
      return;
    }
    const params = new URLSearchParams({ aggregate: 'totals' });
    if (scope) {
      params.set('scope', 'user');
      params.set('viewAs', String(scope.viewAsUserId));
    }
    if (month) {
      params.set('from', `${month}-01`);
      params.set('to', endOfMonth(month));
    }
    apiFetch(`/api/transactions?${params}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`totals ${res.status}`);
        return (await res.json()) as {
          totals: Record<string, number>;
          counts?: Record<string, number>;
        };
      })
      .then((data) => {
        if (cancelled) return;
        setState({
          status: 'ready',
          totals: data.totals ?? {},
          counts: data.counts ?? {},
        });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setState({ status: 'error', totals: {}, counts: {}, error: err.message });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, monthKey, version]);

  return state;
}
